import React, { useContext } from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { LoadingBounce } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { PricesContext } from 'components/PricesContext';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { useGetPrices } from 'hooks/useGetPrices';
import { OrderType } from 'types/OrderType';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import { getDeliveryTaxes, getOrderTAXES, getTotalDeliveryTaxes, getTotalOrderTAX, getTotalPartTAX } from 'utils/taxes';
import Taxes from './Taxes';

interface TotalsProps {
  order: OrderType;
  printMode?: boolean;
  editMode?: boolean;
  commonPricesParams: CommonPricesParamsType;
  componentPlacement: string;
  oldOrder: boolean;
}

export default function Totals(props: TotalsProps) {
  const { t } = useTranslation();
  let totalEdited:
    | {
        total_invoice_price: number;
        totalFinalInvoicePrice: number;
        totalTaxes: number;
      }
    | undefined;

  const { order, printMode, editMode, commonPricesParams, componentPlacement, oldOrder } = props;
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const { selectedTiresAndProducts } = useContext(PricesContext);
  const { setTotalPrice, totalPrice: contextTotalPrice, setSomeEmptyPrice } = useContext(OrderDetailContext);

  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    parts: selectedTiresAndProducts
      ? selectedTiresAndProducts.filter(
          (stp) => !order.parts.some((op) => getMspn(op) === getMspn(stp) && op.pql_pricing && op.manual_pricing),
        )
      : [],
  }); // filter pql manual prices

  if (pricesResponse.data?.parts.some((p) => !p.invoice_price || Number(p.invoice_price) === 0)) {
    setSomeEmptyPrice(true);
    return null;
  }

  if (!pricesResponse.isLoading && pricesResponse.data && selectedTiresAndProducts) {
    const prices = pricesResponse.data.parts;
    const pqlPrices = pricesResponse.data.pqlParts;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const total = { total_invoice_price: 0, totalFinalInvoicePrice: 0, totalTaxes: 0 };
    prices.forEach((fp) => {
      total.total_invoice_price += Number(fp.invoice_price);
      total.totalFinalInvoicePrice += Number(fp.final_invoice_price);
      total.totalTaxes += getTotalPartTAX(fp);
    });
    total.totalTaxes += getTotalDeliveryTaxes(pricesResponse.data);
    if (pqlPrices) {
      pqlPrices.forEach((fp) => {
        total.total_invoice_price += fp.unitAmout;
        total.totalFinalInvoicePrice += fp.totalAmount;
      });
    }
    order.parts.forEach((op) => {
      const selectedFound = selectedTiresAndProducts.find((opql) => getMspn(opql) === getMspn(op));
      if (op.pql_pricing && op.manual_pricing && selectedFound) {
        total.total_invoice_price += Number(op.invoice_price);
        total.totalFinalInvoicePrice += Number(op.invoice_price) * selectedFound.qty;
      }
    });
    totalEdited = total;
  }

  if (
    !order.total_invoice_price ||
    Number.parseFloat(order.total_invoice_price || '0') === 0 ||
    (editMode && !totalEdited)
  )
    return null;

  const fet = editMode ? undefined : Number.parseFloat(order.total_fet || '0').toFixed(2);
  const taxes = editMode ? [] : getOrderTAXES(order);
  const deliveryTaxes = {
    taxes: getDeliveryTaxes(editMode ? pricesResponse?.data : order),
    total: getTotalDeliveryTaxes(editMode ? pricesResponse?.data : order),
  };

  const totalTax = editMode && totalEdited ? totalEdited.totalTaxes : getTotalOrderTAX(order);

  const totalInvoicePrice =
    editMode && totalEdited
      ? Number(totalEdited.total_invoice_price.toFixed(2)) + totalTax
      : Number.parseFloat(order.total_invoice_price || '0').toFixed(2);
  const totalPrice =
    editMode && totalEdited
      ? totalEdited.totalFinalInvoicePrice.toFixed(2)
      : Number.parseFloat(order.total_final_invoice_price || '0').toFixed(2);

  if (!Number(totalPrice)) return null;
  if (Number(totalPrice) !== contextTotalPrice && !oldOrder && editMode) setTotalPrice(Number(totalPrice));

  if (pricesResponse.isLoading) return <LoadingBounce />;
  return (
    <Grid container spacing={2}>
      {!mobileView && <Grid item xs={8} />}
      <Grid item xs={12} style={{ textAlign: 'right' }}>
        {!totalTax && (
          <>
            <Typography
              color={printMode ? undefined : 'primary'}
              style={{ fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              {t('Total invoice price: ${{totalInvoicePrice}}', { totalInvoicePrice })}
            </Typography>
            {Number.parseFloat(fet || '0') > 0 && (
              <Typography style={{ fontSize: 16, whiteSpace: 'nowrap' }}>{t('FET: ${{fet}}', { fet })}</Typography>
            )}
            {taxes.length > 0 && (
              <Taxes taxes={taxes} printMode={printMode} totalTax={totalTax} status={order.status.primary} />
            )}
          </>
        )}
        {!!totalTax && (
          <>
            <Typography
              color={printMode ? undefined : 'primary'}
              style={{ fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              {t('Total invoice price: ${{totalInvoicePrice}}', {
                totalInvoicePrice: (Number.parseFloat(totalPrice || '0') - totalTax).toFixed(2),
              })}
            </Typography>
            {!!deliveryTaxes?.taxes.length && (
              <Taxes
                taxes={deliveryTaxes.taxes}
                label="Delivery Taxes"
                printMode={printMode}
                totalTax={deliveryTaxes.total}
                status={order.status.primary}
              />
            )}
            <Typography style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
              {t('Total estimated taxes: ${{totalTax}}', { totalTax: totalTax.toFixed(2) })}
            </Typography>
            <Typography style={{ fontSize: 10 }}>
              {t('Final tax amounts will be calculated at the time of order completion')}
            </Typography>
          </>
        )}
        <Typography
          style={{ fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap' }}
          id={`Total${componentPlacement}`}
        >
          {t('Total: ${{totalPrice}}', { totalPrice })}
        </Typography>
      </Grid>
    </Grid>
  );
}
